'use client';
/* eslint-disable */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import { styled } from '@mui/material/styles';
import LeftImg from './assets/buttons/left.png';
import LeftHoverImg from './assets/buttons/left-hover.png';
import RightImg from './assets/buttons/right.png';
import RightHoverImg from './assets/buttons/right-hover.png';
import DotActiveImg from './assets/buttons/dot-active.png';
import DotDefaultImg from './assets/buttons/dot.png';
import PauseDefaultImg from './assets/buttons/pause.png';
import PauseHoverImg from './assets/buttons/pause-hover.png';
import PlayDefaultImg from './assets/buttons/play.png';
import PlayHoverImg from './assets/buttons/play-hover.png';
import { sectionSpacingStyles } from '../section-spacing';

const Controls = styled('div')`
  position: absolute;
  display: flex;
  align-items: center;
  margin-top: -15px;
  margin-bottom: -15px;
`;

const ArrowButton = styled('button')<{ direction?: 'left' | 'right' }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 10px;
  position: relative;

  .arrow-hover {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &:hover .arrow-default {
    opacity: 0;
  }

  &:hover .arrow-hover {
    opacity: 1;
  }
`;

const Dots = styled('div')`
  display: flex;
  align-items: center;
`;

const Dot = styled('div')<{ active: boolean }>`
  width: ${props => (props.active ? '24px' : '12px')};
  height: ${props => (props.active ? '24px' : '12px')};
  margin: 0 5px;
  background: ${props =>
    props.active ? `url('${DotActiveImg.src}')` : `url('${DotDefaultImg.src}')`};
  background-size: cover;
  scale: ${props => (props.active ? 1.5 : 1)};
  cursor: pointer;

  &:hover {
    background: url('${DotActiveImg.src}');
    background-size: cover;
    scale: ${props => (props.active ? 1.5 : 2.5)};
  }
`;

const PauseButton = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

// Sample images and titles; replace with real assets
// fallback static images
const defaultImages = [
  { src: '/discover/coaching/coaching-sample.png', title: 'Title 1' },
  { src: '/discover/coaching/coaching-sample2.png', title: 'Title 3' },
  { src: '/discover/coaching/coaching-sample3.png', title: 'Title 4' },
  { src: '/discover/coaching/coaching-sample4.png', title: 'Title 5' },
  { src: '/discover/coaching/coaching-sample2.png', title: 'Title 6' },
  { src: '/discover/coaching/coaching-sample3.png', title: 'Title 7' },
  { src: '/discover/coaching/coaching-sample4.png', title: 'Title 8' },
];

const tileWidth = 330;
const tileGap = 64; // increased gap between tiles to prevent title overlap
const visibleTiles = 4;
const swipeThreshold = 50; // px distance required to treat a gesture as a swipe

export const Carousel: React.FC<{ slides?: any[] }> = ({ slides }) => {
  const [current, setCurrent] = useState(0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  const [viewportWidth, setViewportWidth] = useState<number | null>(null);
  const [mainHover, setMainHover] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchCurrentXRef = useRef<number | null>(null);
  const isMobile = useMediaQuery('(max-width:960px)');

  const step = isMobile ? 1 : visibleTiles;
  // Keep mobile movement at 1 tile, but keep dot indicators grouped like desktop.
  const indicatorStep = visibleTiles;

  // Memoize source images so their identity is stable and we can generate stable keys
  const sourceImages = useMemo(() => {
    const items =
      slides && Array.isArray(slides) && slides.length
        ? slides.map((s: any, i: number) => ({
            src: s.imageUrl || s.image?.asset?._ref || '',
            title: s.title || '',
            key: `orig-${i}`,
          }))
        : defaultImages.map((d, i) => ({ ...d, key: `orig-${i}` }));
    return items;
  }, [slides]);

  // Duplicate first N tiles for seamless loop; give clones distinct stable keys
  const displayImages = useMemo(() => {
    const clones = sourceImages.slice(0, visibleTiles).map((img, i) => ({
      ...img,
      key: `clone-${i}`,
    }));
    return [...sourceImages, ...clones];
  }, [sourceImages]);

  // Precompute random offsets once per display item so animations don't reinitialize on each scroll
  const randomOffsets = useMemo(
    () =>
      displayImages.map(() => ({
        x: (Math.random() - 0.5) * 200,
        y: (Math.random() - 0.5) * 200,
        rotate: (Math.random() - 0.5) * 60,
      })),
    [displayImages.length],
  );

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrent(prev => prev + step);
    }, 10000); // ~10s auto-advance
    return () => clearInterval(interval);
  }, [isPaused, step]);

  useEffect(() => {
    if (!isTransitionEnabled) {
      // After jumping to the start, re-enable transitions for the next slide.
      // A small timeout is needed to ensure the non-transitioned render happens first.
      setTimeout(() => {
        setIsTransitionEnabled(true);
      }, 50);
    }
  }, [isTransitionEnabled]);

  // Detect viewport width to toggle mobile behaviors
  useEffect(() => {
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  const handleTransitionEnd = () => {
    if (current >= sourceImages.length) {
      setIsTransitionEnabled(false);
      setCurrent(0);
    }
  };

  // Slide control functions
  const nextSlide = () => {
    setCurrent(prev => prev + step);
  };

  const prevSlide = () => {
    setCurrent(prev => {
      return prev < step ? sourceImages.length - step : prev - step;
    });
  };

  const togglePause = () => {
    setIsPaused(prev => !prev);
  };

  const resetTouchTracking = () => {
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    touchCurrentXRef.current = null;
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    touchCurrentXRef.current = touch.clientX;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartXRef.current === null) return;
    const touch = event.touches[0];
    touchCurrentXRef.current = touch.clientX;
    // allow touch-action CSS to manage scroll prevention
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartXRef.current === null) {
      resetTouchTracking();
      return;
    }

    const endX = touchCurrentXRef.current ?? event.changedTouches[0]?.clientX ?? 0;
    const endY = event.changedTouches[0]?.clientY ?? touchStartYRef.current ?? 0;
    const deltaX = endX - touchStartXRef.current;
    const deltaY = Math.abs(endY - (touchStartYRef.current ?? endY));

    if (Math.abs(deltaX) > swipeThreshold && Math.abs(deltaX) > deltaY) {
      if (deltaX < 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }

    resetTouchTracking();
  };

  return (
    <Box
      sx={{
        ...sectionSpacingStyles,
        width: '100%',
        position: 'relative',
        overflowX: 'hidden',
        px: { xs: 2, md: 4 },
      }}
    >
      {/* Left ellipse SVG behind content */}

      {/* Right mirrored ellipse SVG behind content */}

      <Box
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        sx={{
          mx: { xs: 1, md: 2 },
          overflow: { xs: 'hidden', md: 'visible' },
          touchAction: 'pan-y',
          userSelect: 'none',
        }}
      >
        <Box
          onTransitionEnd={handleTransitionEnd}
          sx={{
            display: 'flex',
            transition: isTransitionEnabled ? 'transform 0.8s ease-in-out' : 'none',
            transform: `translateX(-${current * (tileWidth + tileGap)}px)`,
          }}
        >
          {displayImages.map((img, idx) => {
            // Stable random offsets for shuffle effect
            const { x: xOffset, y: yOffset, rotate: rotateOffset } = randomOffsets[idx];
            const initialMotion = isMobile
              ? { opacity: 1, x: 0, y: 0, rotate: 0 }
              : { x: xOffset, y: yOffset, opacity: 0, rotate: rotateOffset };

            const animateMotion = isMobile
              ? { opacity: 1, x: 0, y: 0, rotate: 0 }
              : {
                  x: 0,
                  y: 0,
                  opacity: 1,
                  rotate: idx % 2 === 0 ? -3 : 3,
                };

            const transitionMotion = isMobile
              ? { duration: 0 }
              : { duration: 1, delay: idx * 0.08, ease: 'easeInOut' as const };
            return (
              <Box
                key={img.key}
                component={motion.div}
                initial={initialMotion}
                animate={animateMotion}
                transition={transitionMotion}
                sx={{
                  position: 'relative',
                  width: tileWidth,
                  height: 490,
                  bgcolor: '#FDF3DE',
                  flexShrink: 0,
                  borderRadius: '4px',
                  mr: `${tileGap}px`,
                  willChange: 'transform',
                }}
              >
                <Box
                  component="img"
                  src={img.src}
                  alt={img.title}
                  sx={{
                    display: 'block',
                    width: 320,
                    height: 420,
                    mx: 'auto',
                    mt: 0.5,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 64,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      width: '100%',
                      lineHeight: 1.2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-word',
                    }}
                  >
                    {img.title}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* Slide indicator dots and controls */}
      {/* Slide indicator dots and controls */}
      <Controls
        style={{ position: 'static', marginTop: '20px', justifyContent: 'center', width: '100%' }}
      >
        <ArrowButton onClick={prevSlide} direction="left">
          <div className="arrow-default">
            <Image width={64} height={64} src={LeftImg} alt="Previous" />
          </div>
          <div className="arrow-hover">
            <Image width={64} height={64} src={LeftHoverImg} alt="Previous" />
          </div>
        </ArrowButton>

        {!isMobile && (
          <Dots>
            {(() => {
              const pageCount = Math.max(1, Math.ceil(sourceImages.length / indicatorStep));
              const indicatorIndex =
                current >= sourceImages.length ? sourceImages.length - 1 : current;
              const activePage = Math.floor((indicatorIndex % sourceImages.length) / indicatorStep);
              return Array.from({ length: pageCount }).map((_, pageIndex) => (
                <Dot
                  key={pageIndex}
                  active={pageIndex === activePage}
                  onClick={() => {
                    setIsTransitionEnabled(true);
                    setCurrent(pageIndex * indicatorStep);
                  }}
                />
              ));
            })()}
          </Dots>
        )}

        <ArrowButton onClick={nextSlide} direction="right">
          <div className="arrow-default">
            <Image width={64} height={64} src={RightImg} alt="Next" />
          </div>
          <div className="arrow-hover">
            <Image width={64} height={64} src={RightHoverImg} alt="Next" />
          </div>
        </ArrowButton>
      </Controls>

      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.25 }}>
          <PauseButton
            onClick={togglePause}
            onMouseEnter={() => setMainHover(true)}
            onMouseLeave={() => setMainHover(false)}
          >
            {isPaused ? (
              mainHover ? (
                <Image width={104} height={33} src={PlayHoverImg} alt="Play" />
              ) : (
                <Image width={104} height={33} src={PlayDefaultImg} alt="Play" />
              )
            ) : mainHover ? (
              <Image width={104} height={33} src={PauseHoverImg} alt="Pause" />
            ) : (
              <Image width={104} height={33} src={PauseDefaultImg} alt="Pause" />
            )}
          </PauseButton>
        </Box>
      )}
    </Box>
  );
};
